/**
 * Copyright (c) TonTech.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

'use client';

import * as React from 'react';
import { Menu, X } from 'lucide-react';
import { Slot } from 'radix-ui';
import { Button } from '../../ui/button/index';

import { Separator } from '../../ui/separator';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';

import { useIsMobile } from '../../../hooks/use-mobile';
import { cn } from '../../../utils/cn';

const SIDEBAR_WIDTH = '18rem';
const SIDEBAR_COLLAPSED_WIDTH = '4rem';
const SIDEBAR_STORAGE_KEY = 'demo-dapp-sidebar-open';

type SidebarContextProps = {
    open: boolean;
    setOpen: (open: boolean) => void;
    openMobile: boolean;
    setOpenMobile: (open: boolean) => void;
    isMobile: boolean;
    toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

const useSidebar = () => {
    const context = React.useContext(SidebarContext);
    if (!context) {
        throw new Error('useSidebar must be used within a SidebarProvider.');
    }
    return context;
};

const SidebarProvider = ({ className, style, children, ...props }: React.ComponentProps<'div'>) => {
    const isMobile = useIsMobile();
    const [open, setOpen] = React.useState(() => {
        if (typeof window === 'undefined') {
            return true;
        }

        return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) !== '0';
    });
    const [openMobile, setOpenMobile] = React.useState(false);

    const setDesktopOpen = React.useCallback((nextOpen: boolean | ((open: boolean) => boolean)) => {
        setOpen(currentOpen => {
            const resolvedOpen = typeof nextOpen === 'function' ? nextOpen(currentOpen) : nextOpen;

            if (typeof window !== 'undefined') {
                window.localStorage.setItem(SIDEBAR_STORAGE_KEY, resolvedOpen ? '1' : '0');
            }

            return resolvedOpen;
        });
    }, []);

    const toggleSidebar = React.useCallback(() => {
        if (isMobile) {
            setOpenMobile(mobileOpen => !mobileOpen);
        } else {
            setDesktopOpen(desktopOpen => !desktopOpen);
        }
    }, [isMobile, setDesktopOpen]);

    const value = React.useMemo<SidebarContextProps>(
        () => ({
            open,
            setOpen: setDesktopOpen,
            openMobile,
            setOpenMobile,
            isMobile,
            toggleSidebar
        }),
        [open, setDesktopOpen, openMobile, isMobile, toggleSidebar]
    );

    return (
        <SidebarContext.Provider value={value}>
            <div
                data-slot="sidebar-wrapper"
                style={
                    {
                        '--sidebar-width': SIDEBAR_WIDTH,
                        '--sidebar-collapsed-width': SIDEBAR_COLLAPSED_WIDTH,
                        ...style
                    } as React.CSSProperties
                }
                className={cn('flex min-h-svh w-full', className)}
                {...props}
            >
                {children}
            </div>
        </SidebarContext.Provider>
    );
};

const Sidebar = ({ className, children }: React.ComponentProps<'div'>) => {
    const { isMobile, open, openMobile, setOpenMobile } = useSidebar();

    if (isMobile) {
        return (
            <Sheet open={openMobile} onOpenChange={setOpenMobile}>
                <SheetContent
                    side="right"
                    data-slot="sidebar"
                    className="max-w-[85vw] bg-background p-0 text-foreground"
                >
                    <SheetHeader className="sr-only">
                        <SheetTitle>Sidebar</SheetTitle>
                        <SheetDescription>Sidebar navigation.</SheetDescription>
                    </SheetHeader>
                    <div className="flex h-full w-full min-w-0 flex-col overflow-x-hidden">
                        {children}
                    </div>
                </SheetContent>
            </Sheet>
        );
    }

    return (
        <aside
            data-slot="sidebar"
            data-state={open ? 'expanded' : 'collapsed'}
            className={cn(
                'sticky top-0 z-20 flex h-svh shrink-0 flex-col overflow-visible border-r border-tertiary bg-background text-foreground transition-[width] duration-200 ease-out',
                open ? 'w-(--sidebar-width)' : 'w-(--sidebar-collapsed-width)',
                className
            )}
        >
            {children}
        </aside>
    );
};

const SidebarTrigger = ({ className, onClick, ...props }: React.ComponentProps<typeof Button>) => {
    const { toggleSidebar, isMobile, open, openMobile } = useSidebar();
    const isOpen = isMobile ? openMobile : open;
    return (
        <Button
            data-slot="sidebar-trigger"
            size="icon"
            variant="ghost"
            className={className}
            onClick={event => {
                onClick?.(event);
                toggleSidebar();
            }}
            {...props}
        >
            {isOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            <span className="sr-only">Toggle Sidebar</span>
        </Button>
    );
};

const SidebarInset = ({ className, ...props }: React.ComponentProps<'main'>) => {
    return (
        <main
            data-slot="sidebar-inset"
            className={cn('flex min-h-0 min-w-0 flex-1 flex-col', className)}
            {...props}
        />
    );
};

const SidebarHeader = ({ className, ...props }: React.ComponentProps<'div'>) => {
    return (
        <div
            data-slot="sidebar-header"
            className={cn('flex flex-col gap-2 p-2', className)}
            {...props}
        />
    );
};

const SidebarFooter = ({ className, ...props }: React.ComponentProps<'div'>) => {
    return (
        <div
            data-slot="sidebar-footer"
            className={cn('flex flex-col gap-2 p-2', className)}
            {...props}
        />
    );
};

const SidebarContent = ({ className, ...props }: React.ComponentProps<'div'>) => {
    const { isMobile, open } = useSidebar();
    const collapsed = !isMobile && !open;

    return (
        <div
            data-slot="sidebar-content"
            className={cn(
                'flex min-h-0 flex-1 flex-col gap-2',
                collapsed ? 'overflow-visible' : 'overflow-x-hidden overflow-y-auto',
                className
            )}
            {...props}
        />
    );
};

const SidebarGroup = ({ className, ...props }: React.ComponentProps<'div'>) => {
    return (
        <div
            data-slot="sidebar-group"
            className={cn('flex w-full min-w-0 flex-col p-2', className)}
            {...props}
        />
    );
};

const SidebarGroupLabel = ({ className, ...props }: React.ComponentProps<'div'>) => {
    return (
        <div
            data-slot="sidebar-group-label"
            className={cn(
                'overflow-hidden whitespace-nowrap px-2 pb-1 text-[10px] font-medium uppercase tracking-wide text-tertiary-foreground',
                className
            )}
            {...props}
        />
    );
};

const SidebarSeparator = ({ className, ...props }: React.ComponentProps<typeof Separator>) => {
    return (
        <Separator
            data-slot="sidebar-separator"
            className={cn('bg-tertiary data-[orientation=horizontal]:w-auto', className)}
            {...props}
        />
    );
};

const SidebarMenu = ({ className, ...props }: React.ComponentProps<'ul'>) => {
    return (
        <ul
            data-slot="sidebar-menu"
            className={cn('flex w-full min-w-0 flex-col gap-1', className)}
            {...props}
        />
    );
};

const SidebarMenuItem = ({ className, ...props }: React.ComponentProps<'li'>) => {
    return <li data-slot="sidebar-menu-item" className={cn('relative', className)} {...props} />;
};

const SidebarTooltip = ({
    children,
    className,
    ...props
}: React.ComponentProps<'span'> & { children: React.ReactNode }) => {
    return (
        <span
            data-slot="sidebar-tooltip"
            className={cn(
                'pointer-events-none absolute left-[calc(100%+0.75rem)] top-1/2 z-50 hidden -translate-y-1/2 whitespace-nowrap rounded-xl border border-tertiary bg-background px-3 py-2 text-sm font-semibold text-foreground shadow-xl group-hover:block group-has-[:focus-visible]:block',
                className
            )}
            {...props}
        >
            {children}
        </span>
    );
};

const sidebarMenuButtonClasses =
    'flex h-8 w-full shrink-0 items-center gap-2 overflow-hidden rounded-md px-2 py-1 text-left text-sm text-foreground outline-hidden transition-[background-color,color,width,padding] duration-200 ease-out hover:bg-tertiary focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50 data-[active=true]:bg-tertiary data-[active=true]:font-medium [&_svg]:size-4 [&_svg]:shrink-0 [&>span:last-child]:truncate';

const SidebarMenuButton = ({
    asChild = false,
    isActive = false,
    className,
    ...props
}: React.ComponentProps<'button'> & {
    asChild?: boolean;
    isActive?: boolean;
}) => {
    const Comp = asChild ? Slot.Root : 'button';
    return (
        <Comp
            data-slot="sidebar-menu-button"
            data-active={isActive}
            className={cn(sidebarMenuButtonClasses, className)}
            {...props}
        />
    );
};

export {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarTooltip,
    SidebarProvider,
    SidebarSeparator,
    SidebarTrigger,
    useSidebar
};
